import {EventBus} from '@/handler/event-bus'

export default {
  data () {
    return {
      minimizedModals: [],
      activeModals: [],
      backdropSyncInterval: null,
    }
  },
  created () {    
    EventBus.$on('modal:minimize', modalId => {
      this.minimizeModal(modalId);
    })

    EventBus.$on('modal:restore', modalId => {
      this.restoreModal(modalId);
    })

    // Command Palette (Ctrl+K): antes de navegar a otra acción, minimiza lo
    // que haya quedado abierto en la tab actual en vez de dejarlo apilado
    // detrás del nuevo modal (los kept-alive de DefaultTabsLayout no cierran
    // sus modales al desactivarse). Solo afecta a los que ya optaron por ser
    // minimizables (atributo `minimize` en su modal-header) — uno sin ese
    // atributo (ej. un confirm de borrado) sigue bloqueando como siempre.
    EventBus.$on('modal:minimize-all', () => {
      this.minimizeAllActive();
    })
  },
  mounted() {
    this.setupModalDetection();
    this.$nextTick(() => this.scanForExistingModals());
    this.addModalCSS();
    this.startBackdropSyncLoop();
  },
  methods: {
    createMinimizedContainer() {
      if (document.querySelector('#minimized-container')) return;

      const container = document.createElement('div');
      container.id = 'minimized-container';
      container.style.position = 'fixed';
      container.style.bottom = '0';
      container.style.right = '10px';
      container.style.display = 'flex';
      container.style.gap = '10px';
      container.style.zIndex = '1060'; // Sobre modales pero debajo del modal activo

      document.body.appendChild(container);
    },

    // Mismo lenguaje que el resto del sistema (docs/design-system/color-palette,
    // docs/modal-interfaces): ícono-chip + título sobre superficie clara,
    // nunca una franja sólida oscura con texto blanco -- eso es justo el
    // anti-patrón que ese estándar reemplazó en el resto de los modales.
    addModalCSS() {
      const style = document.createElement('style');
      style.innerHTML = `
        .modal-minimized {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 200px;
          height: 38px;
          padding: 0 10px;
          border: 1.5px solid var(--app-color-border-strong);
          border-radius: 10px 10px 0 0;
          background: var(--app-color-surface);
          color: var(--app-color-text);
          box-shadow: var(--app-shadow-md);
          cursor: pointer;
          overflow: hidden;
        }

        .modal-minimized:hover {
          background: var(--app-color-primary-soft);
          border-color: var(--app-color-primary-200);
        }

        .modal-minimized-icon {
          display: inline-flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border-radius: 6px;
          background: var(--app-color-primary-soft);
          color: var(--app-color-primary);
          font-size: 0.85rem;
        }

        .modal-minimized-title {
          flex: 1 1 auto;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: var(--app-text-sm);
          user-select: none;
        }

        .modal-minimized .modal-restore {
          flex: 0 0 auto;
          background: transparent;
          border: none;
          color: var(--app-color-text-secondary);
          cursor: pointer;
          font-weight: bold;
        }

        .modal-minimized .modal-restore:hover {
          color: var(--app-color-primary);
        }

        .modal.minimized {
          display: none !important;
        }

        .modal-backdrop.minimized {
          display: none !important;
        }
      `;
      document.body.appendChild(style);
    },
    
    setupModalDetection() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Buscar modales
              if (node.classList && node.classList.contains('modal')) {
                this.processModal(node);
              } else {
                const modals = node.querySelectorAll('.modal');
                modals.forEach(modal => this.processModal(modal));
              }
              
              // Buscar backdrops
              if (node.classList && node.classList.contains('modal-backdrop')) {
                this.processBackdrop(node);
              } else {
                const backdrops = node.querySelectorAll('.modal-backdrop');
                backdrops.forEach(backdrop => this.processBackdrop(backdrop));
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.modalObserver = observer;
    },
    
    scanForExistingModals() {
      const existingModals = document.querySelectorAll('.modal');
      existingModals.forEach(modal => this.processModal(modal));
      
      const existingBackdrops = document.querySelectorAll('.modal-backdrop');
      existingBackdrops.forEach(backdrop => this.processBackdrop(backdrop));
    },
    
    processModal(modal) {
      if (modal.hasAttribute('data-processed')) return;
      
      modal.setAttribute('data-processed', 'true');
      
      const modalId = modal.id || `modal-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      if (!modal.id) modal.id = modalId;

      // Limpiar al cerrarse
      modal.addEventListener('hidden.bs.modal', () => {
        this.activeModals = this.activeModals.filter(m => m.id !== modalId);
        this.minimizedModals = this.minimizedModals.filter(m => m.id !== modalId);
      });      
      
      // Guardar referencia
      if (!this.activeModals.some(m => m.id === modalId)) {
        this.activeModals.push({
          id: modalId,
          element: modal,
          minimized: false
        });
      }
      
      this.addMinimizeButton(modal, modalId);
    },
    
    addMinimizeButton(modal, modalId) {
      const modalHeader = modal.querySelector('.modal-header');
      if (!modalHeader) return;

      // Verificar si tiene atributo minimize
      const hasMinimize = Array.from(modalHeader.children).some(child => 
        child.hasAttribute('minimize')
      ) || modalHeader.hasAttribute('minimize') || modal.hasAttribute('minimize');
      if (!hasMinimize) return;        
      
      // Verificar si ya existe el botón
      if (modalHeader.querySelector('.modal-minimize-btn')) return;
      
      const minimizeBtn = document.createElement('button');
      minimizeBtn.type = 'button';
      minimizeBtn.className = 'close modal-minimize-btn';
      minimizeBtn.setAttribute('aria-label', 'Minimize');
      minimizeBtn.setAttribute('title', 'Minimizar');
      minimizeBtn.innerHTML = '&#9473;';
      minimizeBtn.style.marginRight = '15px';
      minimizeBtn.style.marginTop = '5px';
      minimizeBtn.style.fontWeight = 'bold';
      minimizeBtn.style.fontSize = '20px';
      // El header del modal dejó de ser una barra oscura (header-bg-variant
      // "dark"/"danger") con el nuevo estándar de _modal-interfaces.scss —
      // ahora es claro, con un ícono-chip. "white" quedaba invisible ahí.
      minimizeBtn.style.color = 'var(--app-color-text-secondary)'
      minimizeBtn.style.textShadow = 'none';
      minimizeBtn.style.opacity = '1';
      minimizeBtn.style.padding = '0px';
      
      minimizeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.minimizeModal(modalId);
      });
      
      const closeBtn = modalHeader.querySelector('.close');
      if (closeBtn) {
        modalHeader.insertBefore(minimizeBtn, closeBtn);
      } else {
        modalHeader.appendChild(minimizeBtn);
      }
    },
    
    minimizeAllActive() {
      this.activeModals
        .filter(m => !m.minimized && m.element.querySelector('.modal-minimize-btn'))
        .forEach(m => this.minimizeModal(m.id));
    },

    minimizeModal(modalId) {
      // Verificar si ya hay 5 modales minimizados      
      if (this.minimizedModals.length >= 5) {
        // Mostrar mensaje de advertencia
        this.$bvToast.toast('Solo puedes tener un máximo de 5 ventanas minimizadas', {
          title: 'Límite alcanzado',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000
        });
        return; // Salir sin minimizar
      }

      const modalInfo = this.activeModals.find(m => m.id === modalId);
      if (!modalInfo) return;
      
      const modal = modalInfo.element;
      const modalHeader = modal.querySelector('.modal-header');
      // El header estándar (.app-modal-header, ver docs/modal-interfaces) separa
      // ícono y título en nodos propios -- leer el header entero con
      // .textContent también trae el texto-ligature del ícono (AppIcon dibuja
      // el glifo con el propio nombre, ej. "edit", como contenido de texto),
      // quedando "editNueva Factura" en vez de "Nueva Factura".
      const titleEl = modalHeader ? modalHeader.querySelector('.app-modal-header__title') : null;
      const titleText = titleEl ? titleEl.textContent.trim() : (modalHeader ? modalHeader.textContent.replace('━','').trim() : 'Modal minimizado');
      const iconEl = modalHeader ? modalHeader.querySelector('.app-modal-header__icon') : null;
      const iconHTML = iconEl ? iconEl.cloneNode(true).outerHTML.replace('app-modal-header__icon', 'modal-minimized-icon') : '';

      // Marcar como minimizado
      modal.classList.add('minimized');
      modalInfo.minimized = true;
      
      // Ocultar backdrop
      const backdrop = this.getBackdropForModal(modalId);
      if (backdrop) {
        backdrop.classList.add('minimized');
      }
      
      // Crear representación minimizada
      const minimizedModal = document.createElement('div');
      minimizedModal.className = 'modal-minimized';
      minimizedModal.setAttribute('data-modal-id', modalId);
      minimizedModal.innerHTML = `
        ${iconHTML}
        <div class="modal-minimized-title" title='${titleText}'>${titleText}</div>
        <button class="modal-restore" title='Maximizar'>▢</button>
      `;
      
      // Click para restaurar (en toda el área)
      minimizedModal.addEventListener('click', (e) => {
        this.restoreModal(modalId);
      });
                
      this.createMinimizedContainer();
      const container = document.querySelector('#minimized-container');

      // Encontrar el índice actual en activeModals (orden lógico)
      const modalIndex = this.activeModals.findIndex(m => m.id === modalId);

      // Buscar el siguiente modal minimizado más adelante en el orden
      const nextMinimized = this.activeModals
        .slice(modalIndex + 1)
        .map(m => m.id)
        .find(id => this.minimizedModals.some(min => min.id === id));

      if (nextMinimized) {
        const nextObj = this.minimizedModals.find(m => m.id === nextMinimized);
        const nextEl = nextObj ? nextObj.minimizedElement : null;

        if (nextEl) {
          container.insertBefore(minimizedModal, nextEl);
        } else {
          container.appendChild(minimizedModal);
        }
      } else {
        container.appendChild(minimizedModal);
      }

      
      // Guardar referencia
      if (!this.minimizedModals.some(m => m.id === modalId)) {
        this.minimizedModals.push({
          id: modalId,
          element: modal,
          minimizedElement: minimizedModal
        });
      }

      // Verificar si todos los modales están minimizados para quitar modal-open
      const allMinimized = this.activeModals.every(m => m.minimized);
      if (allMinimized) {
        document.body.classList.remove('modal-open');
      }      
      
      EventBus.$emit('modal:minimized', modalId);
    },
    
    restoreModal(modalId) {
      const minimizedInfo = this.minimizedModals.find(m => m.id === modalId);
      if (!minimizedInfo) return;
      
      // Eliminar representación minimizada
      const container = document.querySelector('#minimized-container');
      if (minimizedInfo.minimizedElement && container && container.contains(minimizedInfo.minimizedElement)) {
        container.removeChild(minimizedInfo.minimizedElement);
      }
      if (container && container.children.length === 0) {
        container.remove();
      }      
      
      // Restaurar modal
      const modal = minimizedInfo.element;
      modal.classList.remove('minimized');
      
      // Restaurar backdrop
      const backdrop = this.getBackdropForModal(modalId);
      if (backdrop) {
        backdrop.classList.remove('minimized');
      }
      
      // Actualizar estado
      const modalInfo = this.activeModals.find(m => m.id === modalId);
      if (modalInfo) modalInfo.minimized = false;
      
      // Eliminar de minimizados
      this.minimizedModals = this.minimizedModals.filter(m => m.id !== modalId);

      // Asegurar que body tenga la clase modal-open cuando se restaura
      document.body.classList.add('modal-open');      
      
      EventBus.$emit('modal:restored', modalId);
    },
    
    processBackdrop(backdrop) {
      // Si hay modales minimizados, ocultar este backdrop
      if (this.minimizedModals.length > 0) {
        backdrop.classList.add('minimized');
      }
    },

    getBackdropForModal(modalId) {      
      const backdropId = `${modalId}___BV_modal_backdrop_`;
      return document.getElementById(backdropId);
    },
    
    startBackdropSyncLoop() {      
      if (this.backdropSyncInterval) return;

      this.backdropSyncInterval = setInterval(() => {
        this.cleanupDestroyedModals();
        
        if (this.activeModals.length === 0) return;

        const allMinimized = this.activeModals.every(m => m.minimized);
        const backdrops = document.querySelectorAll('.modal-backdrop');

        backdrops.forEach(backdrop => {
          if (allMinimized) {
            backdrop.classList.add('minimized');
          }
          // No quitamos la clase acá, eso lo maneja `restoreModal()`
        });
      }, 300);
    },
    
    cleanupDestroyedModals() {
      // Limpiar modals activos
      this.activeModals = this.activeModals.filter(m => document.body.contains(m.element));
      
      // Limpiar modals minimizados
      this.minimizedModals = this.minimizedModals.filter(m => {
        // Verificar si el modal principal aún existe
        const modalExists = document.body.contains(m.element);
        // Verificar si el elemento minimizado aún existe en el contenedor
        const minimizedElementExists = m.minimizedElement && document.body.contains(m.minimizedElement);
        
        // Si no existe ninguno de los dos, eliminarlo
        if (!modalExists && !minimizedElementExists) {
          return false;
        }
        
        // Si el modal principal no existe pero el minimizado sí, limpiar el minimizado
        if (!modalExists && minimizedElementExists) {
          const container = document.querySelector('#minimized-container');
          if (container && container.contains(m.minimizedElement)) {
            container.removeChild(m.minimizedElement);
          }
          return false;
        }
        
        return true;
      });
      
      // Eliminar contenedor si está vacío
      const container = document.querySelector('#minimized-container');
      if (container && container.children.length === 0) {
        container.remove();
      }

      // Actualizar clase modal-open según el estado de los modales
      const hasVisibleModals = this.activeModals.some(m => !m.minimized);
      if (hasVisibleModals) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }      
    }  
  },
  
  beforeDestroy() {
    if (this.modalObserver) {
      this.modalObserver.disconnect();
    }

    if (this.backdropSyncInterval) {
      clearInterval(this.backdropSyncInterval);
    }    
    
    // Limpiar eventos
    EventBus.$off('modal:minimize');
    EventBus.$off('modal:restore');
  }
}